import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table, Spinner } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { paginateTags } from 'redux/actions/tagActions';

import { ReactComponent as TagIcon } from 'assets/icon-tag.svg';
import { ReactComponent as EditIcon } from 'assets/icon-edit.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';

import { ButtonIcon } from 'shared/components/Buttons';
import ConfirmModal from 'shared/components/ConfirmModal';
import AlertModal from 'shared/components/AlertModal';
import { colors } from 'shared/defaultTheme';
import Pagination from 'shared/components/Pagination';

import { TdActions } from './styles';

const PAGE_SIZE = 10;

const TagsTable = ({ loading, setCurrentTag, setOpenFormModal, refreshTagsData }) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const theme = useSelector((state) => state.theme);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const dispatch = useDispatch();
  const tagState = useSelector((state) => state.tagState);
  const { mainColor } = theme;
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [SelectedRemoveTag, setSelectedRemoveTag] = useState(null);
  const [showConfirmRemoveTag, setShowConfirmRemoveTag] = useState(false);
  const [showSuccessRemove, setShowSuccessRemove] = useState();
  const [showErrorRemove, setShowErrorRemove] = useState(false);
  const { tags, currentTags } = tagState;
  const [{ loading: lodingDelete }, deleteTag] = customAreaApi(
    {
      method: 'DELETE',
    },
    { manual: true }
  );

  const onToggleErrorRemove = () => setShowErrorRemove((currentShow) => !currentShow);

  const onToggleSuccessRemove = () => setShowSuccessRemove((currentShow) => !currentShow);

  const onEdit = (currentTag) => {
    setCurrentTag(currentTag);
    setOpenFormModal(true);
  };

  const onRemove = (currentNotification) => {
    setSelectedRemoveTag(currentNotification);
    setShowConfirmRemoveTag(true);
  };

  const onOkSuccessRemove = () => {
    setSelectedRemoveTag(null);
    onToggleSuccessRemove();
    refreshTagsData();
  };

  const onCancelRemove = () => {
    setShowConfirmRemoveTag(false);
    setSelectedRemoveTag(null);
  };

  const removeSuccess = () => {
    try {
      if (SelectedRemoveTag !== undefined && SelectedRemoveTag !== null) {
        setShowConfirmRemoveTag(false);
        onToggleSuccessRemove();
      }
    } catch (ex) {
      throw new Error(ex);
    }
  };

  const onOkRemove = async () => {
    try {
      const { clientNavisionId, id } = SelectedRemoveTag;
      if (lodingDelete) {
        return;
      }
      const {
        data: { errors },
      } = await deleteTag({ url: `${window.API_V1}/cus/tags/${clientNavisionId}/${id}` });
      if (errors.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      removeSuccess();
    } catch (ex) {
      setShowConfirmRemoveTag(false);
      onToggleErrorRemove();
    }
  };

  const onFirstPage = () => {
    dispatch(paginateTags({ page: 1, pageSize: PAGE_SIZE }));
    setCurrentPage(1);
  };

  const onLastPage = () => {
    dispatch(paginateTags({ page: lastPage, pageSize: PAGE_SIZE }));
    setCurrentPage(lastPage);
  };

  const onNextPage = () => {
    const page = currentPage + 1;
    if (page > lastPage) {
      return;
    }
    dispatch(paginateTags({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onPreviousPage = () => {
    const page = currentPage - 1;
    if (page <= 0) {
      return;
    }
    dispatch(paginateTags({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  const onGoToPage = (page) => {
    dispatch(paginateTags({ page, pageSize: PAGE_SIZE }));
    setCurrentPage(page);
  };

  useEffect(() => {
    const currentLastPage = Math.ceil(tags.length / PAGE_SIZE);
    onFirstPage();
    setLastPage(currentLastPage);
  }, [tags]);

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  return (
    <div className="control-panel__table">
      {!loading ? (
        <Table>
          <thead>
            <tr>
              <th className="text-uppercase">{t('general_tags')}</th>
            </tr>
          </thead>
          <tbody>
            {currentTags.map((tag) => (
              <tr key={tag.id}>
                <td aria-hidden="true" onClick={() => onEdit(tag)} className="link-name-tag">
                  {getNameSelectedLanguage(tag.i18List)}
                </td>
                <TdActions className="d-flex justify-content-end">
                  <ButtonIcon
                    type="button"
                    Icon={() => <EditIcon fill={colors.suvaGrey} />}
                    className="button-icon"
                    onClick={() => onEdit(tag)}
                  />
                  <ButtonIcon
                    type="button"
                    onClick={() => onRemove(tag)}
                    Icon={() => <TrashIcon fill={colors.suvaGrey} />}
                    className="button-icon mr-n3 ml-2"
                  />
                </TdActions>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div className="d-flex justify-content-center mb-5">
          <Spinner color={mainColor} />
        </div>
      )}
      {tags.length === 0 && (
        <div className="panel_control__empty-folder d-flex flex-column align-items-center">
          <TagIcon fill={colors.suvaGrey} width="71" />
          <span>{t('tags_not_found')}</span>
        </div>
      )}
      {tags.length > 0 && (
        <Pagination
          titlePagination={t('Settings_labels')}
          currentPage={currentPage}
          lastPage={lastPage}
          totalData={tags.length}
          onFirstPage={onFirstPage}
          onLastPage={onLastPage}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          onGoToPage={onGoToPage}
        />
      )}
      <ConfirmModal
        isOpen={showConfirmRemoveTag}
        toggle={onCancelRemove}
        confirmMessage={t('custom_feild_delete_field_confirmation').replace(
          '{fieldName}',
          getNameSelectedLanguage(SelectedRemoveTag?.i18List)
        )}
        cancelText={t('general_button_no')}
        okText={t('general_button_yes')}
        onCancel={onCancelRemove}
        onOk={onOkRemove}
      />
      <AlertModal
        type="error"
        isOpen={showErrorRemove}
        toggle={onToggleErrorRemove}
        message={t('custom_field_delete_error_message')}
        okText={t('general_button_ok')}
        onOk={onToggleErrorRemove}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessRemove}
        toggle={onOkSuccessRemove}
        message={t('custom_feild_delete_field_success').replace(
          '{fieldName}',
          getNameSelectedLanguage(SelectedRemoveTag?.i18List)
        )}
        okText={t('general_button_ok')}
        onOk={onOkSuccessRemove}
      />
    </div>
  );
};

TagsTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  setCurrentTag: PropTypes.func.isRequired,
  setOpenFormModal: PropTypes.func.isRequired,
  refreshTagsData: PropTypes.func.isRequired,
};

export default TagsTable;
